import React from "react";
import { CardComp } from "../../../../components";
import moment from "moment";
import styled from "styled-components";

export default function MemberEventDetailsModal({ events = [], selectedDate, onClose }) {
  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";
    return moment(dateString).format("DD-MMM-YYYY");
  };

  const formatTime = (dateString) => {
    if (!dateString) return "Not specified";
    return moment(dateString).format("hh:mm A");
  };

  const getEventStatus = (event) => {
    if (!event?.start_date || !event?.end_date) return "Unknown";

    const now = new Date();
    const startDate = new Date(event.start_date);
    const endDate = new Date(event.end_date);

    if (now < startDate) {
      return "Upcoming";
    } else if (now >= startDate && now <= endDate) {
      return "Ongoing";
    } else {
      return "Past";
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      Upcoming: {
        bg: "#FFA500",
        text: "Upcoming",
      },
      Ongoing: {
        bg: "#19BC31",
        text: "Ongoing",
      },
      Past: {
        bg: "#A1A0A0",
        text: "Past",
      },
      Unknown: {
        bg: "#A1A0A0",
        text: "Unknown",
      },
    };

    const config = statusConfig[status] || statusConfig["Unknown"];

    return (
      <StatusBadge bg={config.bg}>
        {config.text}
      </StatusBadge>
    );
  };

  if (!selectedDate || events.length === 0) {
    return null;
  }

  return (
    <ModalContent>
      <div className="mb-3">
        <h5 className="fw-bold text-black1 mb-2">
          Events on {moment(selectedDate).format("DD MMMM YYYY")}
        </h5>
        <p className="text-muted small mb-0">
          {events.length} {events.length === 1 ? "event" : "events"} scheduled
        </p>
      </div>

      <EventsList>
        {events.map((event, index) => {
          const status = getEventStatus(event);
          const isMultiDay =
            moment(event.end_date).diff(moment(event.start_date), "days") > 0;

          return (
            <EventCard key={event.id || index}>
              <CardComp cardClassName="border-0 shadow-sm mb-3">
                <div className="p-3">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="flex-grow-1">
                      <h6 className="fw-bold text-black1 mb-1">
                        {event.name || "Untitled Event"}
                      </h6>
                      {getStatusBadge(status)}
                    </div>
                  </div>

                  {event.description && (
                    <div className="mb-3">
                      <p className="text-muted small mb-0">{event.description}</p>
                    </div>
                  )}

                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <div className="d-flex align-items-center">
                        <i className="fas fa-calendar text-primary1 me-2"></i>
                        <div>
                          <small className="text-muted d-block">Start Date</small>
                          <span className="fw-medium text-black1">
                            {formatDate(event.start_date)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <div className="d-flex align-items-center">
                        <i className="fas fa-calendar-check text-primary1 me-2"></i>
                        <div>
                          <small className="text-muted d-block">End Date</small>
                          <span className="fw-medium text-black1">
                            {formatDate(event.end_date)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {isMultiDay && (
                      <div className="col-12">
                        <div className="d-flex align-items-center">
                          <i className="fas fa-clock text-primary1 me-2"></i>
                          <div>
                            <small className="text-muted d-block">Duration</small>
                            <span className="fw-medium text-black1">
                              {moment(event.end_date).diff(
                                moment(event.start_date),
                                "days"
                              ) + 1}{" "}
                              {moment(event.end_date).diff(
                                moment(event.start_date),
                                "days"
                              ) === 0
                                ? "day"
                                : "days"}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {event.venue && (
                      <div className="col-12">
                        <div className="d-flex align-items-center">
                          <i className="fas fa-map-marker-alt text-primary1 me-2"></i>
                          <div>
                            <small className="text-muted d-block">Venue</small>
                            <span className="fw-medium text-black1">{event.venue}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardComp>
            </EventCard>
          );
        })}
      </EventsList>
    </ModalContent>
  );
}

const ModalContent = styled.div`
  max-height: 70vh;
  overflow-y: auto;
`;

const EventsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const EventCard = styled.div`
  transition: transform 0.2s ease;
  &:hover {
    transform: translateY(-2px);
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  color: white;
  background-color: ${(props) => props.bg};
  white-space: nowrap;
`;
